/**
 * Analysis Modes Configuration
 * SLAM Sports AI - THE WIRE Multi-Modal Analysis System
 *
 * Supports dual-mode analysis:
 * 1. Opponent Scouting - Identify exploitable tells in opponent pitcher mechanics
 * 2. Big League Hitter Assessment - Evaluate elite hitting potential via temporal-spatial markers
 * 3. Deep Analysis Report - Comprehensive analysis with PDF export
 */

/**
 * Deep Analysis System Prompt - Generates structured markdown for PDF conversion
 * This prompt instructs the AI to produce a comprehensive, professionally-formatted report
 */
const DEEP_ANALYSIS_SYSTEM_PROMPT = `You are an elite sports video analyst generating a comprehensive professional report. Your analysis must be thorough, actionable, and formatted for professional presentation.

**CRITICAL REQUIREMENT: Every finding, observation, and recommendation MUST be linked to specific video timestamps (MM:SS format). Coaches will use this report during film sessions and need to pull up exact moments. An observation without a timestamp is incomplete.**

## OUTPUT FORMAT
Generate your analysis in the following EXACT markdown structure. Do not deviate from this format:

# Deep Analysis Report

## Executive Summary
Provide a 2-3 paragraph overview of the key findings from this video analysis. Include:
- The primary finding that impacts performance — first seen at [MM:SS]
- Secondary observations of note
- One actionable insight the viewer can implement immediately — see [MM:SS]

## Technical Analysis

### Movement & Mechanics
Provide detailed breakdown of observed mechanics, body positioning, timing, and technique. Be specific about:
- Stance and setup position — [MM:SS]
- Movement patterns and transitions — [MM:SS to MM:SS]
- Timing and rhythm observations — [MM:SS]
- Technique execution quality — [MM:SS]

### Performance Metrics
Create a table of quantifiable observations with specific timestamps:
| Metric | Observation | Timestamp | Rating (1-10) |
|--------|-------------|-----------|---------------|
| [Specific metric] | [Detailed observation] | [MM:SS] | [Score] |

Include at least 5-7 metrics based on what you observe in the video.

### Patterns & Tendencies
Identify recurring patterns observed across the footage:
- Consistent behaviors (positive or negative) — seen at [MM:SS, MM:SS, MM:SS]
- Situational tendencies — [MM:SS]
- Predictable sequences — [MM:SS to MM:SS]

## Strengths
List exactly 3 key strengths with detailed explanations:
1. **[Strength Name]**: [Detailed explanation of why this is a strength and its impact] — see [MM:SS]
2. **[Strength Name]**: [Detailed explanation] — see [MM:SS]
3. **[Strength Name]**: [Detailed explanation] — see [MM:SS]

## Areas for Improvement
List exactly 3 areas for improvement with specific remediation:
1. **[Area]**: [What needs improvement and specific steps to address it] — see [MM:SS]
2. **[Area]**: [What needs improvement and specific steps to address it] — see [MM:SS]
3. **[Area]**: [What needs improvement and specific steps to address it] — see [MM:SS]

## Recommendations

### Immediate Actions (Next 24 Hours)
- [Specific actionable item] — Review video at [MM:SS] showing the issue
- [Another immediate action item] — Reference [MM:SS] for correct form

### Short-Term Development (1-2 Weeks)
- [Development focus area with specific drill] — Work on the pattern visible at [MM:SS to MM:SS]
- [Another short-term development item] — Use [MM:SS] as reference footage

### Long-Term Goals (1-3 Months)
- [Strategic objective with measurable target] — Build on the strength shown at [MM:SS]
- [Another long-term goal]

## Film Session Guide
Top 5 timestamps for coaching staff film review:
| # | Timestamp | What to Show | Coaching Point |
|---|-----------|-------------|----------------|
| 1 | [MM:SS] | [Key observation] | [What to teach] |
| 2 | [MM:SS] | [Key observation] | [What to teach] |
| 3 | [MM:SS] | [Key observation] | [What to teach] |
| 4 | [MM:SS] | [Key observation] | [What to teach] |
| 5 | [MM:SS] | [Key observation] | [What to teach] |

## Conclusion
Provide a final summary paragraph that:
- Synthesizes the key findings
- Emphasizes the most important takeaway
- Ends with an encouraging or motivational note

---
*Report generated by SLAM Sports AI Deep Analysis*

IMPORTANT: Follow this structure exactly. Use proper markdown formatting. Every observation MUST cite timestamps — this is a film-based report.`;

export const ANALYSIS_MODES = {
  'simple-mechanics': {
    id: 'simple-mechanics',
    name: 'Quick Mechanics',
    shortName: 'Quick',
    description: 'Fast, simple mechanical breakdown of pitcher delivery',
    icon: 'zap',
    color: '#00d4ff',
    accentColorClass: 'text-cyan-400',
    placeholder: 'Quick mechanical analysis...',
    defaultPrompt: 'Provide a simple, focused mechanical analysis of this pitcher. Include specific video timestamps (MM:SS) for each mechanical observation.',
    sportCategory: 'baseball',
    isSimpleMode: true,  // Flag for simple analysis (uses gemini-2.5-flash, single prompt)
    forceModel: 'flash', // Always use gemini-2.5-flash for speed
  },
  'pitch-tipping': {
    id: 'pitch-tipping',
    name: 'Opponent Scouting',
    shortName: 'Opponent Scouting',
    description: 'Identify exploitable tells in opponent pitcher mechanics for hitter advantage',
    icon: 'baseball',
    color: '#ff6b6b',
    accentColorClass: 'text-red-500',
    placeholder: 'Analyze opponent pitcher for exploitable tells...',
    defaultPrompt: 'Analyze this opponent pitcher footage for exploitable mechanical tells our hitters can use. Focus on glove position, timing variations, arm slot differences, and body positioning patterns that reveal pitch type before release. Identify the highest-value exploitation opportunities. CRITICAL: For every tell or pattern identified, provide specific video timestamps (MM:SS) showing exactly where it occurs. Include a Video Review Sequence of the top 5 timestamps for a pre-game film session with hitters.',
    sportCategory: 'baseball',  // Sport-specific: only show in baseball context
  },
  'big-league-hitter': {
    id: 'big-league-hitter',
    name: 'Big League Hitter Assessment',
    shortName: 'Big League Hitter',
    description: 'Evaluate elite hitting potential via temporal-spatial reasoning markers',
    icon: 'bat',
    color: '#00ff88',
    accentColorClass: 'text-green-500',
    placeholder: 'Analyze batter mechanics for elite potential...',
    defaultPrompt: 'Analyze this batting footage for markers of elite-level temporal-spatial reasoning. Evaluate head stability, decision timing, hip-eye coupling, and swing mechanics that distinguish MLB-caliber hitters. Include specific video timestamps (MM:SS) for each observation and a Film Session Guide of the top 5 timestamps to review.',
    sportCategory: 'baseball',  // Sport-specific: only show in baseball context
  },
  'basketball-analysis': {
    id: 'basketball-analysis',
    name: 'Basketball Analysis',
    shortName: 'General',
    description: 'Analyze shooting mechanics, footwork, and movement patterns',
    icon: 'basketball',
    color: '#FFD700',
    accentColorClass: 'text-yellow-500',
    placeholder: 'Analyze basketball performance...',
    defaultPrompt: 'Analyze this basketball footage for shooting mechanics, footwork efficiency, and movement patterns. Focus on release consistency, arc angle, balance, and decision-making cues.',
    sportCategory: 'basketball',
  },
  'basketball-biomechanics': {
    id: 'basketball-biomechanics',
    name: 'Biomechanics Analysis',
    shortName: 'Biomechanics',
    description: 'Analyze movement efficiency, body mechanics, and injury prevention',
    icon: 'basketball',
    color: '#FFD700',
    accentColorClass: 'text-yellow-500',
    placeholder: 'Analyze basketball biomechanics...',
    defaultPrompt: 'Analyze this basketball footage for biomechanical efficiency. Focus on joint angles during movement, landing mechanics, deceleration patterns, hip-knee alignment, and upper body posture. Identify any movement patterns that could lead to injury or reduced performance. Provide specific recommendations for movement optimization.',
    sportCategory: 'basketball',
  },
  'basketball-shooting': {
    id: 'basketball-shooting',
    name: 'Shooting Mechanics',
    shortName: 'Shooting Form',
    description: 'Evaluate shot release, arc, footwork, and consistency',
    icon: 'basketball',
    color: '#FFD700',
    accentColorClass: 'text-yellow-500',
    placeholder: 'Analyze shooting mechanics...',
    defaultPrompt: 'Analyze this shooting footage for mechanics optimization. Evaluate set point height, release timing, ball rotation, arc angle (target 45-50 degrees), follow-through consistency, and base alignment. Compare across multiple shots to identify patterns affecting accuracy. Provide a shot-by-shot breakdown with specific corrections.',
    sportCategory: 'basketball',
  },
  'basketball-team-defense': {
    id: 'basketball-team-defense',
    name: 'Team Defense Analysis',
    shortName: 'Team Defense',
    description: 'Evaluate defensive rotations, help-side positioning, and communication',
    icon: 'basketball',
    color: '#FFD700',
    accentColorClass: 'text-yellow-500',
    placeholder: 'Analyze team defensive schemes...',
    defaultPrompt: 'Analyze this game footage for defensive effectiveness. Focus on rotation timing, help-side positioning, closeout angles, switching decisions, and communication breakdowns. Identify patterns the offense is exploiting and recommend specific adjustments. Rate each possession defensively and highlight the 3 biggest improvement opportunities.',
    sportCategory: 'basketball',
  },
  'basketball-game-film': {
    id: 'basketball-game-film',
    name: 'Full Game Analysis',
    shortName: 'Game Film',
    description: 'Comprehensive game breakdown with play-by-play tactical analysis',
    icon: 'basketball',
    color: '#FFD700',
    accentColorClass: 'text-yellow-500',
    placeholder: 'Analyze full game footage...',
    defaultPrompt: 'Analyze this full game footage comprehensively. Break down offensive sets (pick-and-roll coverage, motion offense execution, transition opportunities), defensive schemes (man-to-man switches, zone rotations), and lineup effectiveness. Identify the 5 most impactful plays and tactical adjustments made during the game. Provide coaching recommendations with specific timestamps for film review sessions.',
    sportCategory: 'basketball',
  },
  'deep-analysis-baseball': {
    id: 'deep-analysis-baseball',
    name: 'Deep Analysis Report',
    shortName: 'Deep Report',
    description: 'Comprehensive analysis with downloadable PDF report',
    icon: 'document',
    color: '#FFD700',
    accentColorClass: 'text-yellow-500',
    placeholder: 'Generate comprehensive analysis report...',
    defaultPrompt: DEEP_ANALYSIS_SYSTEM_PROMPT,
    isDeepAnalysis: true,
    sportCategory: 'baseball',
  },
  'deep-analysis-basketball': {
    id: 'deep-analysis-basketball',
    name: 'Deep Analysis Report',
    shortName: 'Deep Report',
    description: 'Comprehensive analysis with downloadable PDF report',
    icon: 'document',
    color: '#FFD700',
    accentColorClass: 'text-yellow-500',
    placeholder: 'Generate comprehensive analysis report...',
    defaultPrompt: DEEP_ANALYSIS_SYSTEM_PROMPT,
    isDeepAnalysis: true,
    sportCategory: 'basketball',
  },
  // ─── Golf Analysis Modes ─────────────────────────────────────────────
  'golf-swing': {
    id: 'golf-swing',
    name: 'Full Swing Analysis',
    shortName: 'Swing',
    description: 'Complete swing breakdown using P-position framework (P1-P10) with kinematic sequence analysis',
    icon: 'golf',
    color: '#10b981',
    accentColorClass: 'text-emerald-400',
    placeholder: 'Analyze golf swing mechanics through P1-P10 positions...',
    defaultPrompt: 'Analyze this golf swing footage using the P-position framework (P1 through P10). Evaluate address setup, backswing plane, X-Factor at the top, transition sequencing, delivery position, impact alignments, and follow-through balance. Provide kinematic sequence timing assessment and face-to-path relationship analysis.',
    sportCategory: 'golf',
  },
  'golf-biomechanics': {
    id: 'golf-biomechanics',
    name: 'Biomechanics Analysis',
    shortName: 'Biomechanics',
    description: 'Joint kinematics, VDPID ground reaction forces, and injury prevention assessment',
    icon: 'golf',
    color: '#10b981',
    accentColorClass: 'text-emerald-400',
    placeholder: 'Analyze golf biomechanics and ground reaction forces...',
    defaultPrompt: 'Analyze this golf footage for biomechanical efficiency. Evaluate joint angles at each P-position, estimate ground reaction forces using VDPID (Vision-Derived Predictive Inverse Dynamics), track center of mass displacement, quantify pelvis-thorax dissociation, and assess injury risk for lower back, lead wrist, trail elbow, and cervical spine.',
    sportCategory: 'golf',
  },
  'golf-kinematic-sequence': {
    id: 'golf-kinematic-sequence',
    name: 'Kinematic Sequence',
    shortName: 'Kinematic Seq',
    description: 'Pelvis-Thorax-Arm-Club energy transfer chain and firing order analysis',
    icon: 'golf',
    color: '#10b981',
    accentColorClass: 'text-emerald-400',
    placeholder: 'Analyze kinematic sequence and energy transfer...',
    defaultPrompt: 'Analyze the kinematic sequence of this golf swing. Evaluate peak angular velocities and timing for each segment (Pelvis, Thorax, Lead Arm, Clubhead), identify energy leaks and deceleration patterns, quantify X-Factor Stretch, and assess compliance with the Summation of Speeds principle. Compare timing gaps to PGA Tour averages.',
    sportCategory: 'golf',
  },
  'golf-short-game': {
    id: 'golf-short-game',
    name: 'Short Game Analysis',
    shortName: 'Short Game',
    description: 'Chipping, pitching, bunker play, and putting mechanics evaluation',
    icon: 'golf',
    color: '#10b981',
    accentColorClass: 'text-emerald-400',
    placeholder: 'Analyze short game technique and distance control...',
    defaultPrompt: 'Analyze this short game footage for technique quality. Evaluate address position, wrist action (hinge-and-hold vs release), angle of attack, dynamic loft control, spin rate estimation, distance control consistency, trajectory management, and putting mechanics including face alignment, stroke path, and speed control.',
    sportCategory: 'golf',
  },
  'golf-course-management': {
    id: 'golf-course-management',
    name: 'Course Management',
    shortName: 'Course Mgmt',
    description: 'Shot selection, risk-reward analysis, and strategic decision-making',
    icon: 'golf',
    color: '#10b981',
    accentColorClass: 'text-emerald-400',
    placeholder: 'Analyze course management and strategic decisions...',
    defaultPrompt: 'Analyze the course management and strategic decision-making in this golf footage. Evaluate shot shape selection, risk-reward probability analysis, miss-side awareness, scoring zone targeting, club selection relative to conditions, pre-shot routine quality, decision-making under pressure, and Strokes Gained framework thinking.',
    sportCategory: 'golf',
  },
  'deep-analysis-golf': {
    id: 'deep-analysis-golf',
    name: 'Deep Analysis Report',
    shortName: 'Deep Report',
    description: 'Comprehensive golf analysis with downloadable PDF report',
    icon: 'document',
    color: '#10b981',
    accentColorClass: 'text-emerald-400',
    placeholder: 'Generate comprehensive golf analysis report...',
    defaultPrompt: DEEP_ANALYSIS_SYSTEM_PROMPT,
    isDeepAnalysis: true,
    sportCategory: 'golf',
  },
  // ─── Soccer Analysis Modes ────────────────────────────────────────────
  'soccer-tactical': {
    id: 'soccer-tactical',
    name: 'Tactical Analysis',
    shortName: 'Tactical',
    description: 'Formation analysis, pitch control, pressing patterns, and team shape',
    icon: 'soccer',
    color: '#3b82f6',
    accentColorClass: 'text-blue-400',
    placeholder: 'Describe specific tactical elements to focus on...',
    defaultPrompt: 'Analyze team formation, pressing structure, build-up patterns, and spatial control. Evaluate transition speed, compactness, and pitch control distribution.',
    sportCategory: 'soccer',
  },
  'soccer-biomechanics': {
    id: 'soccer-biomechanics',
    name: 'Biomechanics Analysis',
    shortName: 'Biomechanics',
    description: 'Kinematic load, fatigue indicators, injury risk, and movement efficiency',
    icon: 'soccer',
    color: '#3b82f6',
    accentColorClass: 'text-blue-400',
    placeholder: 'Describe movement patterns or injury concerns to evaluate...',
    defaultPrompt: 'Assess player movement mechanics including sprint profiles, change-of-direction efficiency, deceleration patterns, and injury risk indicators.',
    sportCategory: 'soccer',
  },
  'soccer-attacking': {
    id: 'soccer-attacking',
    name: 'Attacking Patterns',
    shortName: 'Attacking',
    description: 'Chance creation, expected goals (xG), EPV, and attacking buildup',
    icon: 'soccer',
    color: '#3b82f6',
    accentColorClass: 'text-blue-400',
    placeholder: 'Describe attacking phases or patterns to evaluate...',
    defaultPrompt: 'Evaluate attacking patterns including chance creation, expected goals (xG), off-ball movement, and final-third entries.',
    sportCategory: 'soccer',
  },
  'soccer-defensive': {
    id: 'soccer-defensive',
    name: 'Defensive Organization',
    shortName: 'Defensive',
    description: 'Pressing triggers, defensive block, recovery runs, and duel analysis',
    icon: 'soccer',
    color: '#3b82f6',
    accentColorClass: 'text-blue-400',
    placeholder: 'Describe defensive situations or concerns to analyze...',
    defaultPrompt: 'Analyze defensive organization including pressing triggers, block compactness, recovery runs, and 1v1 duel outcomes.',
    sportCategory: 'soccer',
  },
  'soccer-set-pieces': {
    id: 'soccer-set-pieces',
    name: 'Set Piece Analysis',
    shortName: 'Set Pieces',
    description: 'Corner kicks, free kicks, throw-ins, and dead-ball tactical analysis',
    icon: 'soccer',
    color: '#3b82f6',
    accentColorClass: 'text-blue-400',
    placeholder: 'Describe set piece situations or routines to break down...',
    defaultPrompt: 'Break down set piece execution including corner delivery, free kick technique, marking assignments, and rehearsed routine identification.',
    sportCategory: 'soccer',
  },
  'deep-analysis-soccer': {
    id: 'deep-analysis-soccer',
    name: 'Deep Analysis Report',
    shortName: 'Deep Report',
    description: 'Comprehensive soccer analysis with downloadable PDF report',
    icon: 'document',
    color: '#3b82f6',
    accentColorClass: 'text-blue-400',
    placeholder: 'Generate comprehensive soccer analysis report...',
    defaultPrompt: 'Perform a comprehensive tactical, biomechanical, and strategic analysis of the match or session with actionable coaching recommendations.',
    isDeepAnalysis: true,
    sportCategory: 'soccer',
  },
};

/**
 * Get mode configuration by ID
 * @param {string} mode
 * @returns {Object}
 */
export function getModeConfig(mode) {
  return ANALYSIS_MODES[mode];
}

/**
 * Get all available modes as array
 * @returns {Object[]}
 */
export function getAllModes() {
  return Object.values(ANALYSIS_MODES);
}

/**
 * Get default prompt for a mode
 * @param {string} modeId
 * @returns {string}
 */
export function getDefaultPrompt(modeId) {
  const mode = ANALYSIS_MODES[modeId];
  return mode?.defaultPrompt || '';
}

/**
 * Get modes filtered by sport category
 * @param {string} sportCategory - 'baseball', 'basketball', 'golf', or 'soccer'
 * @returns {Object[]} Array of mode configs matching the sport
 */
export function getModesBySport(sportCategory) {
  return Object.values(ANALYSIS_MODES).filter(
    mode => mode.sportCategory === sportCategory
  );
}

/**
 * Get all mode IDs for a specific sport
 * @param {string} sportCategory - 'baseball', 'basketball', 'golf', or 'soccer'
 * @returns {string[]} Array of mode IDs
 */
export function getModeIdsBySport(sportCategory) {
  return Object.keys(ANALYSIS_MODES).filter(
    key => ANALYSIS_MODES[key].sportCategory === sportCategory
  );
}

/**
 * Check if a mode is a deep analysis mode (triggers PDF generation)
 * @param {string} modeId
 * @returns {boolean}
 */
export function isDeepAnalysisMode(modeId) {
  const mode = ANALYSIS_MODES[modeId];
  return mode?.isDeepAnalysis === true;
}

/**
 * Get the deep analysis system prompt
 * @returns {string}
 */
export function getDeepAnalysisPrompt() {
  return DEEP_ANALYSIS_SYSTEM_PROMPT;
}
